// src/modules/app-views/invoices/components/InvoicePreviewActions.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Button, Space, Divider, Dropdown } from 'antd';
import {
  PrinterOutlined,
  CloudUploadOutlined,
  EditOutlined,
  ContainerOutlined,
  RestOutlined,
  FileProtectOutlined,
  MoreOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  MailOutlined,
  WhatsAppOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { PiLifebuoy } from 'react-icons/pi';
import translateX from 'utils/translateX';
import invoicesService from 'modules/app-views/invoices/service';
import appService from 'services/appService';
import { returnOnlyNumbers } from 'utils/helpers';
import showPreviewNFCeModal from '../pages/Init/modals/PreviewNFCeModal';
import showEmitInvoiceNfceModal from '../pages/Init/modals/EmitInvoiceNfceModal';
import showCancelNfceModal from 'modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/modals/CancelNfceModal';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InvoicePreviewActions = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    selectedDocument,
    updateSelectedDocument,
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    setShow,
    folder,
    refresh,
  } = props;

  const storedCompany = useZustand('auth', 'company');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Note: isEmitting state removed to allow multiple parallel emissions

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  if (!selectedDocument) return null;

  const status = selectedDocument?.status;
  const isAuthorized = status === 'authorized';
  const isDraft = status === 'draft';
  const isRejected = status === 'rejected';
  const isError = status === 'error';
  const isProcessedBySefaz = invoicesService.isProcessedBySefaz(status);
  const isValidated = selectedDocument?.documentValidation?.isValid === true;
  const canEmit = (isDraft || isRejected || isError) && isValidated;
  const needsValidation = (isDraft || isRejected || isError) && !isValidated;

  const isArchiveOrRecycle = folder === 'archive' || folder === 'recycle';
  const isRecycle = folder === 'recycle';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleClose = () => setShow?.(false);

  const handleEdit = () => {
    handleClose();
    setTimeout(() => {
      handleEditDocument?.(selectedDocument);
    }, 100);
  };

  const handlePrint = () => {
    showPreviewNFCeModal({ data: selectedDocument });
  };

  const handleValidate = async () => {
    const result = await invoicesService.validate(selectedDocument?._id);

    if (result?.success) {
      // Merge validation result into the current document to update the drawer
      const updatedDocument = {
        ...selectedDocument,
        documentValidation: {
          ...selectedDocument?.documentValidation,
          isValid: result.isValid,
          errors: result.errors || [],
          warnings: result.warnings || [],
          lastValidatedAt: new Date().toISOString(),
        },
      };
      updateSelectedDocument?.(updatedDocument);
      refresh?.();
    }
  };

  const handleEmit = async () => {
    const invoiceId = selectedDocument?._id;
    const invoiceNumber = selectedDocument?.documentNumber || invoiceId?.slice(-6);

    // Show loading message with unique key for this invoice
    appService.message({
      flag: 'loading',
      text: 'emitting_invoice',
      key: `emit-${invoiceId}`,
      duration: 0, // Keep visible until manually hidden
    });

    // Step 1: Validate if company is eligible to emit this document type
    const validation = await invoicesService.validateEmissionEligibility({
      invoice: selectedDocument,
    });

    if (!validation.valid) {
      // Notification already shown by validateEmissionEligibility
      appService.messageDestroy(`emit-${invoiceId}`);
      return;
    }

    // Step 2: Check recipient document
    const recipient = selectedDocument?.recipient || {};
    const isGenericPerson = recipient?.isGenericPerson || recipient?.isGenericPeople;
    const hasCpf = returnOnlyNumbers(recipient?.cpf?.value || recipient?.cpf);
    const hasCnpj = returnOnlyNumbers(recipient?.cnpj?.value || recipient?.cnpj);
    const hasDocument = hasCpf || hasCnpj;

    // If generic person but has CPF (filled in RecipientCard), skip modal
    // The CPF will be used directly in Step 3 below

    // If generic person without CPF or no document, open modal to ask for CPF
    if ((isGenericPerson && (!hasCpf || hasCpf.length !== 11)) || (!isGenericPerson && !hasDocument)) {
      // Hide loading - modal will handle its own loading
      appService.messageDestroy(`emit-${invoiceId}`);

      showEmitInvoiceNfceModal({
        invoiceData: selectedDocument,
        onSuccess: () => {
          refresh?.();
        },
        onError: () => {
          refresh?.();
        },
      });

      // Close drawer immediately so user can select next document
      handleClose();
      return;
    }

    // Step 3: If has document, emit directly without modal
    // Close drawer immediately and emit in background
    handleClose();

    // Emit in background (don't await)
    emitInvoiceInBackground({
      invoiceId,
      invoiceNumber,
      documentType: selectedDocument?.documentType?.toUpperCase() || 'NFCE',
      cpf: hasCpf || null,
      onSuccess: refresh,
      onError: refresh,
    });
  };

  const handleArchive = () => {
    handleArchiveDocuments?.([selectedDocument], {
      onSuccess: () => {
        handleClose();
        refresh?.();
      },
    });
  };

  const handleRemove = () => {
    handleRemoveDocuments?.([selectedDocument], {
      onSuccess: () => {
        handleClose();
        refresh?.();
      },
    });
  };

  const handleRestore = () => {
    handleRestoreDocuments?.([selectedDocument], {
      onSuccess: () => {
        handleClose();
        refresh?.();
      },
    });
  };

  const handleDownloadPdf = () => {
    // NFC-e: FocusNFe serves only HTML; open the browser print dialog on the
    // rendered DANFE so the user can "Salvar como PDF" (crisp vector PDF).
    // NF-e: FocusNFe provides a proper PDF DANFE — fetch it from the backend.
    if (selectedDocument?.documentType === 'nfce') {
      invoicesService.printNfceDanfeToPdf(selectedDocument);
    } else {
      invoicesService.downloadDanfe(selectedDocument?._id);
    }
  };

  const handleDownloadXml = () => {
    invoicesService.downloadXml(selectedDocument?._id);
  };

  const handleSendEmail = () => {
    // TODO: Implement email sending
    console.log('[PREVIEW] Send email:', selectedDocument?._id);
  };

  const handleSendWhatsapp = () => {
    // TODO: Implement WhatsApp sending
    console.log('[PREVIEW] Send WhatsApp:', selectedDocument?._id);
  };

  const handleCancel = () => {
    showCancelNfceModal({
      company: storedCompany,
      invoiceId: selectedDocument?._id,
      invoiceNumber: selectedDocument?.documentNumber,
      isNfce: selectedDocument?.documentType === 'nfce' || selectedDocument?.documentType === 'NFCe',
      onSuccess: () => {
        refresh?.();
        handleClose();
      },
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. BUILD ACTIONS
  // ─────────────────────────────────────────────────────────────────────────────
  // Primary actions (always visible as buttons)
  const primaryActions = [];

  // Archive/Recycle folder actions
  if (isArchiveOrRecycle) {
    primaryActions.push({
      key: 'restore',
      label: translateX('restore'),
      icon: <PiLifebuoy />,
      onClick: handleRestore,
    });
    primaryActions.push({
      key: 'remove',
      label: isRecycle ? translateX('permanently_remove') : translateX('remove'),
      icon: <RestOutlined />,
      onClick: handleRemove,
      danger: isRecycle,
    });
  } else {
    // Documents folder actions
    if (isAuthorized) {
      primaryActions.push({
        key: 'print',
        label: translateX('print'),
        icon: <PrinterOutlined />,
        onClick: handlePrint,
        type: 'primary',
      });
    } else if (canEmit) {
      primaryActions.push({
        key: 'emit',
        label: translateX('emit_invoice'),
        icon: <CloudUploadOutlined />,
        onClick: handleEmit,
        type: 'primary',
      });
    } else if (needsValidation) {
      primaryActions.push({
        key: 'validate',
        label: translateX('validate_invoice'),
        icon: <FileProtectOutlined />,
        onClick: handleValidate,
        type: 'primary',
      });
    }

    primaryActions.push({
      key: 'edit',
      label: translateX('edit'),
      icon: <EditOutlined />,
      onClick: handleEdit,
    });
  }

  // Secondary actions (in MORE dropdown for documents folder)
  const secondaryActions = [];

  if (!isArchiveOrRecycle) {
    if (isAuthorized) {
      secondaryActions.push(
        {
          key: 'pdf',
          label: translateX('download_pdf'),
          icon: <FilePdfOutlined />,
          onClick: handleDownloadPdf,
        },
        {
          key: 'xml',
          label: translateX('download_xml'),
          icon: <FileTextOutlined />,
          onClick: handleDownloadXml,
        },
        { type: 'divider' },
        {
          key: 'email',
          label: translateX('send_by_email'),
          icon: <MailOutlined />,
          onClick: handleSendEmail,
        },
        {
          key: 'whatsapp',
          label: translateX('send_by_whatsapp'),
          icon: <WhatsAppOutlined />,
          onClick: handleSendWhatsapp,
        },
        { type: 'divider' },
        {
          key: 'cancel',
          label: translateX('cancel_invoice'),
          icon: <StopOutlined />,
          onClick: handleCancel,
          danger: true,
        }
      );
    }

    // Archive and Remove for non-processed documents
    if (!isProcessedBySefaz) {
      if (secondaryActions.length > 0) {
        secondaryActions.push({ type: 'divider' });
      }
      secondaryActions.push(
        {
          key: 'archive',
          label: translateX('archive'),
          icon: <ContainerOutlined />,
          onClick: handleArchive,
        },
        {
          key: 'remove',
          label: translateX('remove'),
          icon: <RestOutlined />,
          onClick: handleRemove,
          danger: true,
        }
      );
    }
  }

  // Build menu items for dropdown
  const menuItems = secondaryActions.map(action => {
    if (action.type === 'divider') {
      return { type: 'divider', key: `divider-${Math.random()}` };
    }
    return {
      key: action.key,
      icon: action.icon,
      label: action.label,
      danger: action.danger,
      disabled: action.disabled,
      onClick: action.onClick,
    };
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <Divider style={{ margin: '16px 0' }} />

      <Space orientation="vertical" size={8} style={{ width: '100%' }}>
        {/* Primary action buttons */}
        {primaryActions.map(action => (
          <Button
            key={action.key}
            type={action.type || 'default'}
            icon={action.icon}
            onClick={action.onClick}
            disabled={action.disabled}
            danger={action.danger}
            loading={action.loading}
            block
          >
            {action.label}
          </Button>
        ))}

        {/* More actions dropdown */}
        {menuItems.length > 0 && (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button icon={<MoreOutlined />} block>
              {translateX('more_actions')}
            </Button>
          </Dropdown>
        )}
      </Space>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InvoicePreviewActions;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Emit invoice in background - allows user to continue while emission is processing
 * @param {Object} options
 * @param {string} options.invoiceId - Invoice ID
 * @param {string} options.invoiceNumber - Invoice number for display
 * @param {string} options.documentType - Document type (NFE, NFCE)
 * @param {string} options.cpf - CPF (optional)
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 */
async function emitInvoiceInBackground({ invoiceId, invoiceNumber, documentType, cpf, onSuccess, onError }) {
  try {
    // Call emit service
    const result = await invoicesService.emit(invoiceId, documentType, { cpf });

    if (result?.success) {
      // Replace loading message with success message (same key)
      appService.message({
        flag: 'success',
        text: 'invoice_emitted_successfully',
        key: `emit-${invoiceId}`,
      });

      // Call success callback (e.g., refresh list)
      onSuccess?.(result);
    } else {
      // Close loading message
      appService.messageDestroy(`emit-${invoiceId}`);

      const errorMessage = result?.message || result?.error || 'emit_invoice_error';
      appService.notification({
        flag: 'error',
        title: 'emit_invoice_error',
        description: `#${invoiceNumber}: ${errorMessage}`,
        noTranslateDescription: true,
      });

      // Call error callback (e.g., refresh list)
      onError?.();
    }
  } catch (error) {
    console.error('[InvoicePreviewActions] Emit error:', error);

    // Close loading message
    appService.messageDestroy(`emit-${invoiceId}`);

    appService.notification({
      flag: 'error',
      title: 'emit_invoice_error',
      description: `#${invoiceNumber}: ${error?.message || 'unknown_error'}`,
      noTranslateDescription: true,
    });

    // Call error callback (e.g., refresh list)
    onError?.();
  }
}
